/**
 * KATALOGEN OVER MULIGE KOLONNER, OG REGLENE SOM LAR ET LAGRET VALG OVERLEVE AT DEN ENDRES.
 *
 * =================================================================================================
 * DETTE ER DELEN SOM KAN BLI FEIL UTEN AT NOE SIER FRA, og derfor er den en ren funksjon.
 *
 * Alt annet i kolonnevelgeren sier fra når det ryker: knappen som ikke åpner seg, raden som ikke
 * lar seg dra, tabellen som mangler en kolonne man nettopp huket av. `resolveColumnPrefs` gjør
 * ikke det. Den kjører i det sekundet en side lastes, mot et valg som ble lagret i forrige uke og
 * en katalog som er endret siden — og resultatet av at den tar feil er en tabell som ser HELT
 * NORMAL ut, med én kolonne for lite eller én for mye. Ingen feilmelding, ingen rød test.
 *
 * To regler, og begge er skrevet ned i kontrakten (`packages/shared/src/contracts/table-prefs.ts`):
 *
 *   · EN LAGRET NØKKEL SOM IKKE FINNES I KATALOGEN IGNORERES. Kolonnen ble fjernet fra produktet;
 *     brukeren skal ikke måtte rydde etter oss, og en tabell som kaster fordi den leste en gammel
 *     rad er en side som ikke kan åpnes.
 *
 *   · EN NY KOLONNE I KATALOGEN LEGGES TIL SKJULT BAKERST. Ikke synlig: en kolonne som dukker opp
 *     av seg selv i en tabell noen har stilt inn er en tabell som flytter på seg. Bakerst og ikke
 *     på katalogplassen sin, fordi rekkefølgen er BRUKERENS — å sette den inn midt i lista ville
 *     dyttet på det hen har ordnet.
 *
 * =================================================================================================
 * KATALOGEN ER KODENS, OG STANDARDSETTET ER DEN SLIK DEN STÅR.
 *
 * `inDefault` sier hvilke kolonner som er PÅ når ingen har valgt noe, og katalogens egen
 * rekkefølge er standardrekkefølgen. Det er derfor det ikke finnes et eget «standardsett» å
 * vedlikeholde ved siden av: standarden er dagens tabell, skrevet én gang.
 *
 * Bredde og sortérbarhet står også her og ikke i kolonnedefinisjonen, av samme grunn — ordren sier
 * «kolonnebredder følger katalogen; ingen manuell breddejustering», og to steder å skrive en
 * bredde er ett sted for mye.
 */
import type { TableColumnPref } from '@tvitvi/shared';
import type { ReactNode } from 'react';

import type { TKey } from '@/i18n/types';

import type { TableAlign, TableColumn } from './Table';

export interface ColumnCatalogueEntry {
  /** Kodens nøkkel, den som lagres: «commissionBooking», ikke «Provisjonssats booking». */
  key: string;
  /** i18n-nøkkelen. En etikett er aldri en streng i katalogen (prinsipp 8). */
  labelKey?: TKey;
  /**
   * Etiketten ferdig oversatt — det `Table` gir når katalogen bygges av kolonnene selv
   * (`catalogueFromColumns`), der overskriften allerede er en streng. Vinner over `labelKey`.
   */
  label?: string;
  /**
   * Kolonnen kan ikke skjules (navnet i et register). Står i velgeren med haken låst på, og et
   * lagret valg som sier «skjult» leses som synlig — ellers ville en gammel rad skjult den.
   */
  locked?: boolean;
  /** `'112px'`. Utelatt lar kolonnen ta plassen innholdet trenger. */
  width?: string;
  /**
   * Gulvet kolonnen ikke klemmes under — `Table`s `minWidth` (QA-runde 12, 24c/9b → J5).
   *
   * Den manglet her mens `Table` alt hadde den, og følgen var at en kolonne i en katalogstyrt
   * tabell bare kunne få et TAK: registeret ga byrået `width: '112px'` og fikk «Standing Ov…» på
   * hver rad. Katalogen må kunne si det samme som tabellen, ellers er valget mellom «låst bredde»
   * og «ingen bredde».
   */
  minWidth?: string;
  sortable?: boolean;
  numeric?: boolean;
  align?: TableAlign;
  startsWith?: 'asc' | 'desc';
  headerHidden?: boolean;
  /** Med i standardsettet — altså synlig når ingenting er lagret. Rekkefølgen er katalogens. */
  inDefault?: boolean;
}

export type ColumnCatalogue = readonly ColumnCatalogueEntry[];

/** Én celle, per katalognøkkel. Skilt fra katalogen fordi en celle er JSX og en katalog er data. */
export type ColumnCells<Row> = Record<string, (row: Row) => ReactNode>;

/** Katalogens eget svar: dagens kolonner i dagens rekkefølge. */
export function defaultColumnPrefs(catalogue: ColumnCatalogue): TableColumnPref[] {
  return catalogue.map((entry) => ({
    key: entry.key,
    visible: entry.inDefault === true || entry.locked === true,
  }));
}

/**
 * Det lagrede valget, lest mot katalogen slik den er NÅ.
 *
 * Svaret inneholder alltid nøyaktig katalogens nøkler, én gang hver: det er det som gjør at den
 * som tegner velgeren og den som tegner tabellen leser den samme lista.
 *
 * En TOM lagret liste betyr «ingen preferanse» — den samme tilstanden som en manglende rad — og
 * gir katalogens standard. Det er derfor «Tilbakestill» sletter raden framfor å lagre standarden:
 * en lagret kopi av gårsdagens standardsett ville stått fast den dagen katalogen fikk en kolonne.
 */
export function resolveColumnPrefs(
  catalogue: ColumnCatalogue,
  saved: readonly TableColumnPref[] | null | undefined,
): TableColumnPref[] {
  if (saved === null || saved === undefined || saved.length === 0) {
    return defaultColumnPrefs(catalogue);
  }

  const known = new Map(catalogue.map((entry) => [entry.key, entry]));
  const taken = new Set<string>();
  const resolved: TableColumnPref[] = [];

  for (const one of saved) {
    /* Ukjent nøkkel: kolonnen finnes ikke lenger. Hoppes over, aldri en feil. */
    const entry = known.get(one.key);
    if (entry === undefined) continue;
    /*
     * Den samme nøkkelen to ganger ville tegnet den samme kolonnen to ganger — og med
     * `rowId`-en fra `Table` som React-nøkkel er det en advarsel i konsollen og en celle for
     * mye. Den første vinner: det er plassen leseren dro den til.
     */
    if (taken.has(one.key)) continue;
    taken.add(one.key);
    resolved.push({ key: one.key, visible: entry.locked === true || one.visible });
  }

  /*
   * Ny kolonne i katalogen: skjult, og bakerst, så ingenting brukeren har ordnet flytter seg.
   * Unntaket er en låst kolonne — den kan ikke være skjult, så den står synlig bakerst.
   */
  for (const entry of catalogue) {
    if (taken.has(entry.key)) continue;
    resolved.push({ key: entry.key, visible: entry.locked === true });
  }

  return resolved;
}

/** Om valget er katalogens eget — det «Tilbakestill til standard» fører tilbake til. */
export function isDefaultColumnPrefs(
  catalogue: ColumnCatalogue,
  prefs: readonly TableColumnPref[],
): boolean {
  const fallback = defaultColumnPrefs(catalogue);
  if (prefs.length !== fallback.length) return false;
  return fallback.every((one, index) => {
    const there = prefs[index];
    return there !== undefined && there.key === one.key && there.visible === one.visible;
  });
}

/** Flytter kolonnen på `from` til `to`, som drahåndtaket gjør. Utenfor lista er ingen endring. */
export function moveColumnPref(
  prefs: readonly TableColumnPref[],
  from: number,
  to: number,
): TableColumnPref[] {
  if (from === to) return [...prefs];
  if (from < 0 || from >= prefs.length || to < 0 || to >= prefs.length) return [...prefs];
  const next = [...prefs];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return [...prefs];
  next.splice(to, 0, moved);
  return next;
}

/** Skrur én kolonne av eller på. Rekkefølgen er urørt — en skjult kolonne beholder plassen sin. */
export function toggleColumnPref(
  prefs: readonly TableColumnPref[],
  key: string,
): TableColumnPref[] {
  return prefs.map((one) => (one.key === key ? { key: one.key, visible: !one.visible } : one));
}

/**
 * Tabellens kolonner: katalogens form, brukerens rekkefølge, sidens celler.
 *
 * En nøkkel uten celle hoppes over framfor å tegne en tom kolonne. Det skal ikke kunne skje —
 * kallstedene skriver `cells` som `Record<Nøkkel, …>` nettopp for at kompilatoren skal si fra —
 * og hvis det likevel skjer, er en kolonne for lite bedre enn en kolonne med ingenting i.
 */
export function buildTableColumns<Row>(
  catalogue: ColumnCatalogue,
  prefs: readonly TableColumnPref[],
  cells: ColumnCells<Row>,
  t: (key: TKey) => string,
): TableColumn<Row>[] {
  const known = new Map(catalogue.map((entry) => [entry.key, entry]));
  const columns: TableColumn<Row>[] = [];

  for (const one of prefs) {
    if (!one.visible) continue;
    const entry = known.get(one.key);
    const cell = cells[one.key];
    if (entry === undefined || cell === undefined) continue;

    columns.push({
      id: entry.key,
      header: catalogueLabel(entry, t),
      cell,
      ...(entry.width === undefined ? {} : { width: entry.width }),
      ...(entry.minWidth === undefined ? {} : { minWidth: entry.minWidth }),
      ...(entry.sortable === undefined ? {} : { sortable: entry.sortable }),
      ...(entry.numeric === undefined ? {} : { numeric: entry.numeric }),
      ...(entry.align === undefined ? {} : { align: entry.align }),
      ...(entry.startsWith === undefined ? {} : { startsWith: entry.startsWith }),
      ...(entry.headerHidden === undefined ? {} : { headerHidden: entry.headerHidden }),
    });
  }

  return columns;
}

/** Etiketten velgeren og overskriften viser: den ferdige strengen først, så nøkkelen. */
export function catalogueLabel(entry: ColumnCatalogueEntry, t: (key: TKey) => string): string {
  if (entry.label !== undefined) return entry.label;
  return entry.labelKey === undefined ? entry.key : t(entry.labelKey);
}

/**
 * Står kolonnen UTENFOR velgeren?
 *
 * En kontrollcelle (stjerna, et drahåndtak) eller en kolonne uten synlig overskrift har ikke noe
 * navn å krysse av for, og skal ikke kunne skjules eller flyttes av en leser som ikke vet hva den
 * er. `pickerLabel` gjør en slik kolonne valgbar likevel, når siden vil det.
 */
export function isFixedColumn<Row>(column: TableColumn<Row>): boolean {
  if (column.pickerLabel !== undefined) return false;
  return column.control === true || column.headerHidden === true;
}

/**
 * Katalogen `Table` bygger av sine egne kolonner når den får en `tableKey`.
 *
 * Nøkkelen er kolonnens `id`, og det er det som gjør at et valg lagret gjennom den gamle veien
 * (`buildTableColumns`, der `id` er katalognøkkelen) leses uendret når en side flyttes over.
 */
export function catalogueFromColumns<Row>(columns: readonly TableColumn<Row>[]): ColumnCatalogue {
  return columns
    .filter((column) => !isFixedColumn(column))
    .map((column) => ({
      key: column.id,
      label: column.pickerLabel ?? column.header,
      inDefault: column.defaultHidden !== true,
      ...(column.hideable === false ? { locked: true } : {}),
    }));
}

/**
 * Kolonnene tabellen faktisk tegner: de faste der de står, de valgbare i leserens rekkefølge.
 *
 * En fast kolonne før den første valgbare blir stående først (stjerna foran navnet), alle andre
 * faste står sist (radens egne knapper). Uten lagret valg (`prefs === null`) er svaret kolonnene
 * slik siden skrev dem, minus de som er skjult som standard.
 */
export function arrangeColumns<Row>(
  columns: readonly TableColumn<Row>[],
  prefs: readonly TableColumnPref[] | null,
): TableColumn<Row>[] {
  if (prefs === null) {
    return columns.filter((column) => isFixedColumn(column) || column.defaultHidden !== true);
  }
  const leading: TableColumn<Row>[] = [];
  const trailing: TableColumn<Row>[] = [];
  const byId = new Map<string, TableColumn<Row>>();
  let seenPickable = false;
  for (const column of columns) {
    if (isFixedColumn(column)) {
      (seenPickable ? trailing : leading).push(column);
    } else {
      seenPickable = true;
      byId.set(column.id, column);
    }
  }
  const middle: TableColumn<Row>[] = [];
  for (const one of prefs) {
    const column = byId.get(one.key);
    if (column === undefined) continue;
    if (one.visible || column.hideable === false) middle.push(column);
  }
  return [...leading, ...middle, ...trailing];
}
